<template>
  <Teleport to="#app">
    <div
      id="wiris-modal-container"
      v-click-outside.capture="handleClickOutside"
      class="wiris-modal-container"
      :class="{ hide: !visible }"
    >
      <div id="wiris-modal-content" class="wiris-modal-content">
        <div id="wiris-hand-input-container" class="wiris-hand-input-container" />
        <div class="wrs_modal_buttons_container">
          <button class="wrs_modal_button_accept" kind="primary" @click="actionPrimary">
            {{ t("wiris_button_insert") }}
          </button>
          <button class="wrs_modal_button_cancel" kind="secondary" @click="remove">
            {{ t("wiris_button_close") }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { clickOutsideDirective } from "@utils/directives";
import appConfigs from "@configs/app";
import DOMUtils from "@utils/dom.utils";
import {
  INPUT_MODE,
  INPUT_STRATEGY,
  INTERACTION_TYPE,
  SOURCE,
} from "@plugins/richtext/plugins/algebra-kit/AlgebraKit.constant";
import wirisApi from "@apis/wiris.api";
import DeviceUtils from "@utils/device.utils";
import { DRAWER_NAME } from "@modules/app/service/app.constant";
import { usePageDrawer } from "@modules/app/composables/page.drawer";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { useVm } from "@utils/vm.ts";

const vClickOutside = clickOutsideDirective["click-outside"];

const { vm } = useVm();
const { t } = useI18n();
const toast = useToast();

// Use page drawer composable
const { handleCollapseDrawer } = usePageDrawer({
  drawerName: DRAWER_NAME.ATTEMPT,
});

// Data
const editor = ref(null);
const visible = ref(false);
const params = ref({ type: Object, default: null });
const akitElement = ref(null);

// Methods
const show = (newParams) => {
  // If modal is already visible and it's previously rendering for different akit input, clear the items first and render again
  if (
    visible.value &&
    akitElement.value?.getAttribute("ref-id") !== newParams.akitElement.getAttribute("ref-id")
  ) {
    remove();
  }
  visible.value = true;

  params.value = newParams;
  akitElement.value = newParams.akitElement;

  // Create Editor if it doesn't exist
  if (!!editor.value) return;

  // Hide drawer on mobile if akit modal is shown
  if (DeviceUtils.isMobile()) {
    handleCollapseDrawer();
  }

  let scriptEl = document.querySelector(`script[src="${appConfigs.wirisEditorScript}"]`);

  if (!scriptEl) {
    scriptEl = document.createElement("script");
    scriptEl.setAttribute("src", appConfigs.wirisEditorScript);
    document.head.appendChild(scriptEl);
    scriptEl.onload = () => {
      initEditor();
    };
  } else {
    initEditor();
  }
};

const initEditor = () => {
  editor.value = com.wiris.js.JsHand.newInstance();
  editor.value.insertInto(getEditorContainer());
  visible.value = true;
};

const remove = () => {
  // Editor is already hidden, ignore the call
  if (!visible.value) return;

  const isHandWritingMode = !!akitElement.value
    ? !!akitElement.value.shadowRoot?.querySelector(".akit-icon-edit")
    : false;

  if (!isHandWritingMode) {
    // Remove and hide contents if the akit is no longer in hand writing mode.
    visible.value = false;
    getEditorContainer().replaceChildren();
    editor.value = null;
    akitElement.value?.setInputMode(INPUT_MODE.KEYBOARD);
  } else {
    // If the handwriting icon during a click detected and remove get triggered, it's due to incorrect input mode from akit
    // Reset it back to handwriting to correct the mode
    akitElement.value?.setInputMode(INPUT_MODE.HANDWRITING);
  }
};

const getEditorContainer = () => {
  return document.getElementById("wiris-hand-input-container");
};

const actionPrimary = () => {
  if (!editor.value) return;

  wirisApi
    .getLatexFromMathML(editor.value.getMathML())
    .then((response) => {
      // Show error toast if response is an error message
      if (response.data && response.data.toString().toLowerCase().indexOf("error") !== -1) {
        showErrorMessage();
        return;
      }
      akitElement.value?.setMathInput(response.data.toString(), {
        inputStrategy: INPUT_STRATEGY.INSERT,
        source: SOURCE.MATH_TYPE,
      });
      editor.value?.setMathML("<math></math>");
    })
    .catch((err) => {
      console.error(err);
      showErrorMessage();
    });
};

const handleClickOutside = (event) => {
  // if akitElement is no longer connected to DOM (e.g., question is submitted), ignore the event
  if (!akitElement.value?.isConnected) {
    return;
  }

  // Close modal if user click on navigational links or open side panel
  const eventOnBtnOrLinks =
    checkComponentTag(event.target, "a") || checkComponentTag(event.target, "button");

  const eventTargetRefId =
    event.target.getAttribute("ref-id") ||
    (event.target.querySelector("akit-interaction")
      ? event.target.querySelector("akit-interaction").getAttribute("ref-id")
      : null);

  const eventTargetSessionId =
    event.target.getAttribute("session-id") ||
    (event.target.querySelector("akit-interaction")
      ? event.target.querySelector("akit-interaction").getAttribute("session-id")
      : null);

  if (eventOnBtnOrLinks) {
    remove();
    return;
  }

  // After handling the navigation links and side panels, we check for interactions within the lesson components

  // #1 Ignore event if user is interacting on the active akit or whitespaces on the screen
  if (
    (!DOMUtils.parent(event.target, ".lesson-viewer-activity-component") &&
      !DOMUtils.parent(event.target, ".quiz-page-component")) ||
    (eventTargetRefId === akitElement.value?.getAttribute("ref-id") &&
      eventTargetSessionId === akitElement.value?.getAttribute("session-id"))
  )
    return;

  // #2 Close modal if user is interacting with non-active AK question (Since #1 would return if it's active), non AK questions or other components
  const clickedInsideAKitComponent = checkComponentTag(event.target, "akit-interaction");

  const clickOnNonAKQuestion =
    // Non AK Question
    (!DOMUtils.parent(event.target, ".algebra-kit-component") &&
      !!DOMUtils.parent(event.target, ".question-component")) ||
    // Non Question Components (Poll, etc)
    !DOMUtils.parent(event.target, ".component.question-component");

  let hasClosedModal = false;

  if ((clickedInsideAKitComponent && visible.value) || clickOnNonAKQuestion) {
    remove();
    hasClosedModal = true;
  }

  // #3 If user clicked on another AK multistep question, show handwriting automatically
  const eventAkitInteraction = document.querySelector(
    `akit-interaction[ref-id="${eventTargetRefId}"][session-id="${eventTargetSessionId}"]`,
  );

  if (!eventAkitInteraction) return;

  eventAkitInteraction.getInteractionType().then((result) => {
    if (
      hasClosedModal &&
      clickedInsideAKitComponent &&
      (eventTargetRefId !== akitElement.value?.getAttribute("ref-id") ||
        eventTargetSessionId !== akitElement.value?.getAttribute("session-id")) &&
      result === INTERACTION_TYPE.MULTISTEP
    ) {
      const akitInteraction = eventAkitInteraction;
      akitInteraction.setInputMode(INPUT_MODE.HANDWRITING);
      vm.$wirisModal?.show({
        akitElement: akitInteraction,
      });
    }
  });
};

const checkComponentTag = (target, selector) => {
  let cssClass = null;
  if (selector.charAt(0) === ".") {
    cssClass = selector.substring(1);
  }
  const parentEl = DOMUtils.parent(target, selector);
  let isDisabled = false;
  if (
    target.classList.contains("disabled") ||
    (parentEl && parentEl.classList.contains("disabled"))
  ) {
    isDisabled = true;
  }
  return (
    (!!DOMUtils.parent(target, selector) ||
      (cssClass && target.classList.contains(cssClass)) ||
      target.tagName.toLowerCase() === selector) &&
    !isDisabled
  );
};

const showErrorMessage = () => {
  toast?.show("error", {
    kind: "error",
    title: t("wiris_latex_error"),
  });
};

defineExpose({
  show,
  remove,
});
</script>

<style lang="scss">
@import "@styles/layout/z-map";
@import "@styles/abstracts/variables";
@import "@styles/layout/breakpoint";

.wiris-modal-container {
  .hide {
    display: none;
  }
}

.wiris-modal-content {
  background: #fafafa;
  position: fixed;
  width: 580px;
  z-index: z-map("wiris-modal");
  bottom: 0;
  right: 10px;
  padding: 6px;

  @include media("mobile") {
    max-width: 100%;
    right: 0;
  }

  .wiris-hand-input-container {
    height: 284px;

    @include media("mobile") {
      max-height: 50%;
    }
  }
}

//Replicated styling from wiris
.wrs_modal_button_accept {
  min-width: 80px;
  font-size: 14px;
  border-radius: 3px;
  border: 1px solid #778e9a;
  padding: 6px 8px;
  margin: 10px auto;
  margin-right: 5px;
  margin-bottom: 0;
  color: #fff;
  background: #778e9a;
  cursor: pointer;
  font-family: arial, sans-serif;
  height: 32px;
}

.wrs_modal_button_cancel {
  min-width: 80px;
  font-size: 14px;
  border-radius: 3px;
  border: 1px solid #778e9a;
  padding: 6px 8px;
  margin: 10px auto;
  margin-left: 5px;
  margin-bottom: 0;
  cursor: pointer;
  font-family: arial, sans-serif;
  background-color: #ddd;
  height: 32px;
}
</style>
